import { PersistedState } from '@/app/(backoffice)/_types/persisted-state';
import { LanguageIsoCode } from '@/graphql/graphql';
import { type ClassValue, clsx } from 'clsx';
import { addMinutes, subMinutes } from 'date-fns';
import { twMerge } from 'tailwind-merge';

export function cn(...inputs: ClassValue[]) {
    return twMerge(clsx(inputs));
}

export function adjustForTimezone(date: Date) {
    const offset = date.getTimezoneOffset();
    return offset > 0 ? subMinutes(date, offset) : addMinutes(date, Math.abs(offset));
}

export const isoEn = LanguageIsoCode.En.toLowerCase() as LanguageIsoCode;
export const isoFr = LanguageIsoCode.Fr.toLowerCase() as LanguageIsoCode;

export function readState(key: string): PersistedState | null {
    try {
        const raw = localStorage.getItem(key);
        return raw ? JSON.parse(raw) : null;
    } catch {
        return null;
    }
}

export function writeState(key: string, toSave: PersistedState): void {
    localStorage.setItem(key, JSON.stringify(toSave));
}

export function uniqBy<T, K>(array: T[], keyFn: (item: T) => K): T[] {
    const seen = new Set<K>();
    const result: T[] = [];

    for (const item of array) {
        const key = keyFn(item);
        if (!seen.has(key)) {
            seen.add(key);
            result.push(item);
        }
    }

    return result;
}

export const getFallbackTitle = (
    titles: Partial<Record<LanguageIsoCode, string>> | null,
    lang: LanguageIsoCode,
    fallbackEn = true,
): string => {
    if (!titles) {
        return '';
    }
    return titles[lang] || (fallbackEn ? titles[isoEn] : '') || '';
};

export function getFileExtensionFromUrl(url?: string | null): string | undefined {
    if (!url) {
        return undefined;
    }

    const extractFromPath = (path: string): string | undefined => {
        const fileName = path.split('/').pop() ?? '';
        const dotIndex = fileName.lastIndexOf('.');

        if (dotIndex <= 0 || dotIndex === fileName.length - 1) {
            return undefined;
        }

        return fileName.slice(dotIndex + 1).toUpperCase();
    };

    try {
        return extractFromPath(new URL(url).pathname);
    } catch {
        const sanitizedUrl = url.split('?')[0]?.split('#')[0] ?? '';
        return extractFromPath(sanitizedUrl);
    }
}
